import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@apollo/client/react";

import { GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";
import AssetImage from "src/components/wizard/Item/AssetImage";
import Header from "src/components/wizard/Header";
import Footer from "src/components/wizard/Footer/Footer";
import useAuth from "src/hooks/useAuth";
import { getCustomDescriptionValue } from "src/utils/asset";

export default function Step2BItemSelection({
  shipment,
  step,
  totalSteps,
  selectedItemIds,
  setSelectedItemIds,
  onBack,
  onNext,
  onClose,
}) {
  const { user } = useAuth();
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { data, loading: loadingItems } = useQuery(GET_SHIPMENT_QUERY, {
    variables: { id: shipment?.id },
    skip: !shipment?.id,
    fetchPolicy: "network-only",
  });

  const shipmentItems = data?.shipment?.shipmentItems || [];
  const totalItems = shipmentItems.length;
  const selectedCount = selectedItemIds.length;
  const allSelected = selectedCount >= totalItems && totalItems > 0;
  const noneSelected = selectedCount === 0;

  const handleToggleItem = (itemId) => {
    setSelectedItemIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const handleSearchToggle = () => {
    setShowSearch(!showSearch);
    if (showSearch) {
      setSearchTerm("");
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter items based on search term
  const filteredItems = shipmentItems.filter((item) => {
    if (!searchTerm.trim()) return true;
    
    const { asset } = item;
    const { itemId, itemCode, barcode, skuNumber, description } = asset;
    const searchLower = searchTerm.toLowerCase();
    
    // Search in main fields
    if (itemId?.toLowerCase().includes(searchLower)) return true;
    if (itemCode?.toLowerCase().includes(searchLower)) return true;
    if (barcode?.toLowerCase().includes(searchLower)) return true;
    if (skuNumber?.toLowerCase().includes(searchLower)) return true;
    if (description?.toLowerCase().includes(searchLower)) return true;
    
    if (getCustomDescriptionValue(asset).toLowerCase().includes(searchLower)) return true;
    
    return false;
  });

  const isNextDisabled = noneSelected || allSelected;

  return (
    <>
      <Header
        title="Select Items to Transfer"
        actions={
          <IconButton size="small" onClick={handleSearchToggle}>
            <SearchIcon />
          </IconButton>
        }
        info={
          <Typography variant="body2" color="textSecondary">
            {selectedCount} selected
          </Typography>
        }
      />
      {showSearch && (
        <Box px={2} pt={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search items..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      <Box p={2} flexGrow={1} sx={{ overflowY: "auto" }}>
        {loadingItems ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {filteredItems.map((item) => {
              const { asset } = item;
                const { itemId, itemCode, barcode, skuNumber, description, images = [] } = asset;
              const assetImage = images.find(img => img.default) || images[0];
              const imageUrl = item.attachments?.[0]?.url || assetImage?.url;
              const title = [itemId, itemCode, barcode, skuNumber].filter(Boolean).join(" | ");
              const isChecked = selectedItemIds.includes(item.id);

              const getDisplayDescription = () => {
                if (description) return description;
                return getCustomDescriptionValue(asset);
              };

              return (
                <Box
                  key={item.id}
                  sx={{
                    borderBottom: "1px solid #E0E0E0",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    ...(isChecked && { backgroundColor: "rgba(25, 118, 210, 0.08)" }),
                  }}
                  onClick={() => handleToggleItem(item.id)}
                >
                  <Box display="flex" my={1.5} width="100%" overflow="hidden" alignItems="center">
                    <Box mr={1} display="flex" alignItems="center">
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{ p: 0.5 }}
                        disableRipple
                      />
                    </Box>
                    <Box mr={3}>
                      <AssetImage width={120} height={100} image={imageUrl} />
                    </Box>
                    <Box display="flex" flexDirection="column" flex={1}>
                      <Typography fontWeight={500} sx={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                      }}>
                        {title}
                      </Typography>
                      <Typography mt={0.5} sx={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                      }}>
                        {getDisplayDescription()}
                      </Typography>
                      {item.quantity === 0 ? (
                        <Box mt={0.5} color="red" fontWeight={500} fontSize="1.0rem">
                          Expected Quantity: {item.expectedQuantity || 0}
                        </Box>
                      ) : (
                        <Box mt={0.5} fontWeight={500} fontSize="1.0rem">
                          Quantity: {item.quantity}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {allSelected && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You cannot select all items. Use "Entire Shipment" transfer instead.
          </Alert>
        )}
      </Box>
      <Footer step={step} steps={totalSteps} showRaceMode={false}>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onNext}
            disabled={isNextDisabled}
          >
            Next
          </Button>
        </Box>
      </Footer>
    </>
  );
}
